import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Overline, FadeIn } from "@/components/site/Shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const services = ["Residential Construction", "Commercial Construction", "Industrial Construction", "Renovation & Remodeling", "Interior Works", "Architectural Design", "Structural Assessment", "Other"];
const slots = ["9:00 AM – 11:00 AM", "11:00 AM – 1:00 PM", "2:00 PM – 4:00 PM", "4:00 PM – 6:00 PM"];

export default function BookSiteVisit() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", service: "", preferred_date: "", time_slot: "", remarks: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service || !form.time_slot) { toast.error("Please select a service and time slot"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/site-visits", fd);
      setReference(data.reference);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to book visit");
    } finally {
      setSending(false);
    }
  };

  if (reference) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center px-5" data-testid="visit-success">
        <div className="border border-line bg-graphite p-12 max-w-xl text-center">
          <CheckCircle2 size={52} className="text-emerald-400 mx-auto mb-6" />
          <h1 className="font-heading text-3xl uppercase tracking-tight text-white">Visit Booked</h1>
          <p className="text-silver mt-4 leading-relaxed">A site visit task has been created in our ERP. Our engineer will call to confirm your slot.</p>
          <div className="border border-ember/40 bg-ember/10 px-6 py-4 mt-8">
            <p className="text-xs uppercase tracking-[0.2em] text-silver">Your Reference</p>
            <p className="font-heading text-2xl text-ember font-semibold mt-1" data-testid="visit-reference">{reference}</p>
          </div>
          <Link to="/" className="inline-block mt-8 text-white uppercase text-xs tracking-[0.2em] font-bold border-b-2 border-ember pb-2 hover:text-ember transition-colors duration-200">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-visit-page">
      <section className="py-24 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <Overline className="mb-5">Book a Site Visit</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95]">
              An Engineer At Your Site.<br /><span className="text-ember">Free Of Charge.</span>
            </h1>
            <p className="text-silver max-w-2xl mt-8 leading-relaxed">
              Choose a date and slot, and a qualified OGK engineer visits your site — measuring, assessing feasibility and answering questions before you commit to anything.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <form onSubmit={submit} className="border border-line bg-graphite p-8 sm:p-12" data-testid="visit-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Full Name *</label>
                <Input required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="visit-name-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Phone *</label>
                <Input required value={form.phone} onChange={set("phone")} placeholder="+91" data-testid="visit-phone-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Email *</label>
                <Input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="visit-email-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Service Required *</label>
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger className="rounded-none bg-carbon border-line h-12" data-testid="visit-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent className="rounded-none bg-graphite border-line">
                    {services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Preferred Date *</label>
                <Input required type="date" value={form.preferred_date} onChange={set("preferred_date")} data-testid="visit-date-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Time Slot *</label>
                <Select value={form.time_slot} onValueChange={(v) => setForm({ ...form, time_slot: v })}>
                  <SelectTrigger className="rounded-none bg-carbon border-line h-12" data-testid="visit-slot-select"><SelectValue placeholder="Select slot" /></SelectTrigger>
                  <SelectContent className="rounded-none bg-graphite border-line">
                    {slots.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-6">
              <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Site Address *</label>
              <Textarea required value={form.address} onChange={set("address")} rows={3} placeholder="Full address with landmark" data-testid="visit-address-input" className="rounded-none bg-carbon border-line" />
            </div>
            <div className="mt-6">
              <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Remarks (Optional)</label>
              <Textarea value={form.remarks} onChange={set("remarks")} rows={3} placeholder="Anything our engineer should know in advance" data-testid="visit-remarks-input" className="rounded-none bg-carbon border-line" />
            </div>
            <div className="mt-8">
              <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Site Photos (Optional)</label>
              <label className="border border-dashed border-line bg-carbon flex flex-col items-center justify-center py-10 cursor-pointer hover:border-ember transition-colors duration-200" data-testid="visit-file-upload">
                <Camera size={24} className="text-ember mb-3" />
                <span className="text-silver text-sm">{files.length > 0 ? `${files.length} photo(s) selected` : "Click to attach site photos"}</span>
                <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} />
              </label>
            </div>
            <Button type="submit" disabled={sending} data-testid="visit-submit-btn"
              className="mt-10 w-full rounded-none bg-ember hover:bg-[#e64a19] text-white uppercase tracking-[0.15em] font-bold h-14 text-sm">
              {sending ? "Booking…" : "Book Site Visit"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
